import React from 'react';
import { useTranslation } from 'react-i18next';
import { DailyReport, WorkflowExecution } from '../types';
import { Clock, CheckCircle, XCircle, PlayCircle, AlertCircle, ChevronRight, Layers } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface ReportListProps {
  report: DailyReport;
  onExecutionSelect: (executionId: string) => void;
}

const STATUS_CONFIG: Record<string, { icon: React.ReactNode; color: string; bg: string; border: string; gradient: string }> = {
  completed: {
    icon: <CheckCircle size={18} />,
    color: '#16a34a',
    bg: '#f0fdf4',
    border: '#bbf7d0',
    gradient: 'linear-gradient(135deg, #22c55e, #16a34a)',
  },
  failed: {
    icon: <XCircle size={18} />,
    color: '#dc2626',
    bg: '#fef2f2',
    border: '#fecaca',
    gradient: 'linear-gradient(135deg, #ef4444, #dc2626)',
  },
  running: {
    icon: <PlayCircle size={18} />,
    color: '#2563eb',
    bg: '#eff6ff',
    border: '#bfdbfe',
    gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)',
  },
  partial: {
    icon: <AlertCircle size={18} />,
    color: '#ea580c',
    bg: '#fff7ed',
    border: '#fed7aa',
    gradient: 'linear-gradient(135deg, #f97316, #ea580c)',
  },
  partially_completed: {
    icon: <AlertCircle size={18} />,
    color: '#ea580c',
    bg: '#fff7ed',
    border: '#fed7aa',
    gradient: 'linear-gradient(135deg, #f97316, #ea580c)',
  },
  pending: {
    icon: <Clock size={18} />,
    color: '#64748b',
    bg: '#f8fafc',
    border: '#e2e8f0',
    gradient: 'linear-gradient(135deg, #94a3b8, #64748b)',
  },
};

export const ReportList: React.FC<ReportListProps> = ({ report, onExecutionSelect }) => {
  const { t } = useTranslation('reports');
  const formatDuration = (execution: WorkflowExecution) => {
    if (!execution.started_at) return 'N/A';
    if (execution.completed_at) {
      const durationMs = new Date(execution.completed_at).getTime() - new Date(execution.started_at).getTime();
      const minutes = Math.floor(durationMs / 60000);
      const seconds = Math.floor((durationMs % 60000) / 1000);
      return `${minutes}m ${seconds}s`;
    }
    return t('running');
  };

  const stats = {
    total: report.executions.length,
    completed: report.executions.filter(e => e.status === 'completed').length,
    failed: report.executions.filter(e => e.status === 'failed').length,
    running: report.executions.filter(e => e.status === 'running').length,
  };

  const statCards = [
    { label: t('statTotal'), value: stats.total, icon: <Layers size={18} />, gradient: 'linear-gradient(135deg, #6366f1, #4f46e5)', bg: '#eef2ff' },
    { label: t('statCompleted'), value: stats.completed, icon: <CheckCircle size={18} />, gradient: 'linear-gradient(135deg, #22c55e, #16a34a)', bg: '#f0fdf4' },
    { label: t('statFailed'), value: stats.failed, icon: <XCircle size={18} />, gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', bg: '#fef2f2' },
    { label: t('statRunning'), value: stats.running, icon: <PlayCircle size={18} />, gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)', bg: '#eff6ff' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* ── Summary Stats ────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '12px',
      }}>
        {statCards.map(s => (
          <div key={s.label} style={{
            background: 'white',
            borderRadius: '14px',
            padding: '16px',
            border: '1px solid #f1f5f9',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}>
            <div style={{
              width: '34px', height: '34px',
              borderRadius: '10px',
              background: s.gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '10px',
              color: 'white',
            }}>
              {s.icon}
            </div>
            <div style={{ fontSize: '26px', fontWeight: 700, color: '#0f172a', lineHeight: 1 }}>
              {s.value}
            </div>
            <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 500, marginTop: '4px' }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* ── Executions ───────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {report.executions.map((execution) => {
          const hasError = execution.status === 'failed' || execution.nodes.some(n => !!n.error || n.status === 'failed');
          const cfg = hasError ? STATUS_CONFIG.failed : (STATUS_CONFIG[execution.status] || STATUS_CONFIG.pending);
          return (
            <button
              key={execution.execution_id}
              onClick={() => onExecutionSelect(execution.execution_id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                padding: '16px 18px',
                background: 'white',
                borderRadius: '14px',
                border: '1px solid #f1f5f9',
                cursor: 'pointer',
                textAlign: 'left',
                width: '100%',
                transition: 'all 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.08)';
                e.currentTarget.style.borderColor = cfg.border;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)';
                e.currentTarget.style.borderColor = '#f1f5f9';
              }}
            >
              {/* Status icon */}
              <div style={{
                width: '40px', height: '40px',
                borderRadius: '12px',
                background: cfg.bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: cfg.color,
                flexShrink: 0,
              }}>
                {cfg.icon}
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {execution.workflow_name}
                </div>
                <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: '#94a3b8', flexWrap: 'wrap' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <Clock size={12} /> {formatDuration(execution)}
                  </span>
                  <span>{execution.nodes.length} nodes</span>
                  <span>
                    {execution.started_at && formatDistanceToNow(new Date(execution.started_at), { addSuffix: true })}
                  </span>
                </div>
              </div>

              {/* Status badge */}
              <span style={{
                fontSize: '12px',
                fontWeight: 600,
                color: cfg.color,
                background: cfg.bg,
                border: `1px solid ${cfg.border}`,
                borderRadius: '8px',
                textTransform: 'uppercase',
              }}>
                {hasError && execution.status !== 'failed' ? 'error' : execution.status}
              </span>

              <ChevronRight size={18} style={{ color: '#cbd5e1', flexShrink: 0 }} />
            </button>
          );
        })}

        {report.executions.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '48px 24px',
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            borderRadius: '16px',
            border: '1px dashed #93c5fd',
          }}>
            <Clock size={40} style={{ color: '#93c5fd', marginBottom: '12px' }} />
            <p style={{ color: '#64748b', fontSize: '14px' }}>No executions found for this date</p>
          </div>
        )}
      </div>
    </div>
  );
};
